import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import Modal from '../components/Modal';
import {
  createWooCommerceCategory,
  deleteWooCommerceCategory,
  getWooCommerceCategoriesDetailed,
  updateWooCommerceCategory,
} from '../components/woocommerceHelper';

const emptyForm = {
  name: '',
  slug: '',
  description: '',
  parent: 0,
  image: '',
};

const OFFICE_IMAGE_UPLOAD_URL = 'https://office.le-celadon.ma/uploadimagesgetUrl.php';
const OFFICE_IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || 'https://office.le-celadon.ma';

const sortByName = (left, right) => {
  return String(left?.name || '').localeCompare(String(right?.name || ''), 'fr', {
    sensitivity: 'base',
  });
};

const flattenCategories = (categories) => {
  const byParent = new Map();

  categories.forEach((category) => {
    const parent = Number(category.parent || 0);
    if (!byParent.has(parent)) {
      byParent.set(parent, []);
    }
    byParent.get(parent).push(category);
  });

  for (const items of byParent.values()) {
    items.sort(sortByName);
  }

  const ordered = [];
  const visited = new Set();

  const walk = (parentId, depth) => {
    const children = byParent.get(parentId) || [];
    children.forEach((category) => {
      if (visited.has(category.id)) {
        return;
      }
      visited.add(category.id);
      ordered.push({ ...category, depth });
      walk(category.id, depth + 1);
    });
  };

  walk(0, 0);

  categories
    .filter((category) => !visited.has(category.id))
    .sort(sortByName)
    .forEach((category) => {
      ordered.push({ ...category, depth: 0 });
      walk(category.id, 1);
    });

  return ordered;
};

const collectDescendantIds = (categories, categoryId) => {
  const childrenByParent = new Map();

  categories.forEach((category) => {
    const parent = Number(category.parent || 0);
    if (!childrenByParent.has(parent)) {
      childrenByParent.set(parent, []);
    }
    childrenByParent.get(parent).push(category.id);
  });

  const descendants = new Set();
  const stack = [categoryId];

  while (stack.length) {
    const currentId = stack.pop();
    const children = childrenByParent.get(currentId) || [];
    children.forEach((childId) => {
      if (!descendants.has(childId)) {
        descendants.add(childId);
        stack.push(childId);
      }
    });
  }

  return descendants;
};

const formatTreeLabel = (category) => {
  return `${'> '.repeat(Math.max(Number(category?.depth || 0), 0))}${category?.name || ''}`;
};

const hideModalById = (modalId) => {
  try {
    const modalElement = document.getElementById(modalId);
    const bs = window.bootstrap;
    if (modalElement && bs) {
      const instance = bs.Modal.getInstance(modalElement) || new bs.Modal(modalElement);
      instance.hide();
    }
  } catch (error) {
    console.warn('Unable to close modal', error);
  }
};

const buildOfficeImageUrl = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }

  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }

  const base = OFFICE_IMAGE_BASE_URL.replace(/\/+$/, '');
  const path = rawValue.replace(/^\/+/, '');
  return `${base}/${path}`;
};

const extractOfficeUploadImageUrl = (payload) => {
  if (typeof payload === 'string') {
    return buildOfficeImageUrl(payload);
  }

  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const candidate =
    payload.url ||
    payload.imageUrl ||
    payload.image_url ||
    payload.location ||
    payload.src ||
    payload.filename ||
    payload.fileName ||
    payload.path;

  return buildOfficeImageUrl(candidate);
};

function WooCategoriesView() {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());

  const loadCategories = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await getWooCommerceCategoriesDetailed();
      setCategories(Array.isArray(response) ? response : []);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Impossible de charger les categories WooCommerce.';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const flattenedCategories = useMemo(() => flattenCategories(categories), [categories]);

  const parentNameById = useMemo(() => {
    return new Map(categories.map((category) => [category.id, category.name]));
  }, [categories]);

  const categoryTree = useMemo(() => {
    const byId = new Map(categories.map((category) => [category.id, category]));
    const childrenByParent = new Map();

    categories.forEach((category) => {
      const parent = Number(category.parent || 0);
      if (!childrenByParent.has(parent)) {
        childrenByParent.set(parent, []);
      }
      childrenByParent.get(parent).push(category);
    });

    for (const children of childrenByParent.values()) {
      children.sort(sortByName);
    }

    const countDescendants = (categoryId, visited = new Set()) => {
      if (visited.has(categoryId)) {
        return 0;
      }
      visited.add(categoryId);
      const children = childrenByParent.get(categoryId) || [];
      return children.reduce((count, child) => count + 1 + countDescendants(child.id, visited), 0);
    };

    const descendantCountById = new Map(
      categories.map((category) => [category.id, countDescendants(category.id)])
    );

    return { byId, childrenByParent, descendantCountById };
  }, [categories]);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const visibleCategoryIds = useMemo(() => {
    if (!normalizedSearchQuery) {
      return null;
    }

    const visibleIds = new Set();
    categories.forEach((category) => {
      const parentName = parentNameById.get(category.parent) || '';
      const haystack = [
        category.name,
        category.slug,
        category.description,
        parentName,
      ].join(' ').toLowerCase();

      if (!haystack.includes(normalizedSearchQuery)) {
        return;
      }

      let current = category;
      while (current) {
        visibleIds.add(current.id);
        current = categoryTree.byId.get(Number(current.parent || 0));
      }
    });

    return visibleIds;
  }, [categories, categoryTree.byId, normalizedSearchQuery, parentNameById]);

  useEffect(() => {
    if (!visibleCategoryIds) {
      return;
    }

    setExpandedCategoryIds((current) => {
      const next = new Set(current);
      visibleCategoryIds.forEach((categoryId) => {
        const children = categoryTree.childrenByParent.get(categoryId) || [];
        if (children.some((child) => visibleCategoryIds.has(child.id))) {
          next.add(categoryId);
        }
      });
      return next;
    });
  }, [categoryTree.childrenByParent, visibleCategoryIds]);

  const availableParentOptions = useMemo(() => {
    if (!editingId) {
      return flattenedCategories;
    }

    const blockedIds = collectDescendantIds(categories, editingId);
    blockedIds.add(editingId);
    return flattenedCategories.filter((category) => !blockedIds.has(category.id));
  }, [categories, editingId, flattenedCategories]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setErrorMessage('');
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      parent: Number(category.parent || 0),
      image: category.image || '',
    });
    setErrorMessage('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: name === 'parent' ? Number(value) || 0 : value,
    }));
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingImage(true);
    setErrorMessage('');

    try {
      const uploadData = new FormData();
      uploadData.append('productName', formData.name || file.name);
      uploadData.append('price', 0);
      uploadData.append('description', formData.description || formData.name || file.name);
      uploadData.append('picture', file);
      uploadData.append('file', file);

      const uploadResponse = await axios.post(
        OFFICE_IMAGE_UPLOAD_URL,
        uploadData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (uploadResponse?.data?.success === false) {
        throw new Error(uploadResponse?.data?.error || 'Upload image invalide');
      }

      const imageUrl = extractOfficeUploadImageUrl(uploadResponse?.data);

      if (!imageUrl) {
        throw new Error(uploadResponse?.data?.error || 'Upload image invalide');
      }

      setFormData((current) => ({
        ...current,
        image: imageUrl,
      }));
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Impossible d uploader l image.';
      setErrorMessage(message);
    } finally {
      event.target.value = '';
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      if (editingId) {
        await updateWooCommerceCategory(editingId, formData);
      } else {
        await createWooCommerceCategory(formData);
      }

      setFormData(emptyForm);
      setEditingId(null);
      await loadCategories();
      hideModalById('wooCategoryModal');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Impossible d enregistrer la categorie WooCommerce.';
      setErrorMessage(message);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    const confirmed = window.confirm('Supprimer cette categorie WooCommerce ?');
    if (!confirmed) {
      return;
    }

    setErrorMessage('');

    try {
      await deleteWooCommerceCategory(categoryId);
      await loadCategories();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Impossible de supprimer la categorie WooCommerce.';
      setErrorMessage(message);
    }
  };

  const toggleCategoryExpanded = (categoryId) => {
    setExpandedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const renderCategoryRows = (parentId = 0, depth = 0) => {
    const rows = [];
    const children = categoryTree.childrenByParent.get(parentId) || [];

    children.forEach((category) => {
      if (visibleCategoryIds && !visibleCategoryIds.has(category.id)) {
        return;
      }

      const childCount = categoryTree.descendantCountById.get(category.id) || 0;
      const hasChildren = childCount > 0;
      const isExpanded = expandedCategoryIds.has(category.id);
      const shouldRenderChildren = hasChildren && isExpanded;

      rows.push(
        <TableRow key={category.id} hover>
          <TableCell>
            {category.image ? (
              <img
                src={category.image}
                alt={category.name}
                style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6 }}
              />
            ) : (
              '-'
            )}
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: depth * 2 }}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={() => toggleCategoryExpanded(category.id)}
                  aria-label={isExpanded ? 'Replier la categorie' : 'Ouvrir la categorie'}
                >
                  {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
              ) : (
                <Box sx={{ width: 34, flexShrink: 0 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {category.name}
              </Typography>
              {hasChildren ? (
                <Chip size="small" label={`${childCount} enfant${childCount > 1 ? 's' : ''}`} />
              ) : null}
            </Stack>
          </TableCell>
          <TableCell>{formatTreeLabel({ ...category, depth })}</TableCell>
          <TableCell>{parentNameById.get(category.parent) || '-'}</TableCell>
          <TableCell align="center">{category.count}</TableCell>
          <TableCell sx={{ maxWidth: 360 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              title={category.description || ''}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {category.description || '-'}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <IconButton
              color="primary"
              size="small"
              data-bs-toggle="modal"
              data-bs-target="#wooCategoryModal"
              onClick={() => handleEdit(category)}
              aria-label="Modifier la categorie"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(category.id)}
              aria-label="Supprimer la categorie"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      );

      if (hasChildren) {
        rows.push(
          <TableRow key={`${category.id}-children`}>
            <TableCell colSpan={7} sx={{ p: 0, borderBottom: shouldRenderChildren ? undefined : 0 }}>
              <Collapse in={shouldRenderChildren} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>{renderCategoryRows(category.id, depth + 1)}</TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        );
      }
    });

    return rows;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Categories WooCommerce
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gerez le nom, l arborescence et l image des categories du site WooCommerce.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCategories}
            disabled={loading || saving}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            data-bs-toggle="modal"
            data-bs-target="#wooCategoryModal"
            onClick={handleOpenCreate}
          >
            Ajouter une categorie
          </Button>
        </Stack>
      </Stack>

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Rechercher une categorie"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Nom, slug, description ou parent"
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {visibleCategoryIds ? `${visibleCategoryIds.size} resultat(s)` : `${categories.length} categorie(s)`}
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Arborescence</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell align="center">Produits</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!categories.length ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {loading ? 'Chargement...' : 'Aucune categorie WooCommerce trouvee.'}
                  </TableCell>
                </TableRow>
              ) : visibleCategoryIds && visibleCategoryIds.size === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Aucun resultat pour cette recherche.
                  </TableCell>
                </TableRow>
              ) : renderCategoryRows()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        modalId="wooCategoryModal"
        modalType="modal-xl"
        onSubmit={handleSubmit}
        submitLabel={saving ? 'Enregistrement...' : editingId ? 'Modifier' : 'Ajouter'}
        submitDisabled={saving || !formData.name.trim()}
        title={editingId ? 'Modifier une categorie WooCommerce' : 'Ajouter une categorie WooCommerce'}
      >
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Nom"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="optionnel"
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Image URL"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://..."
            />
            <TextField
              select
              fullWidth
              label="Categorie parente"
              name="parent"
              value={formData.parent}
              onChange={handleChange}
              SelectProps={{
                native: true,
              }}
            >
              <option value={0}>Aucune</option>
              {availableParentOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {formatTreeLabel(category)}
                </option>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingImage || saving}
            >
              {uploadingImage ? 'Upload en cours...' : 'Uploader une image'}
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={handleImageFileChange}
              />
            </Button>
            {formData.image ? (
              <Button
                variant="text"
                color="error"
                onClick={() => setFormData((current) => ({ ...current, image: '' }))}
                disabled={uploadingImage || saving}
              >
                Retirer l image
              </Button>
            ) : null}
          </Stack>

          {formData.image ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Apercu image
              </Typography>
              <img
                src={formData.image}
                alt="Apercu categorie"
                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8 }}
              />
            </Box>
          ) : null}

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
        </Box>
      </Modal>
    </Box>
  );
}

export default WooCategoriesView;
